import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Login from './pages/Login';
import Dashboard from './modules/dashboard/Dashboard';
import GuestDashboard from './modules/dashboard/GuestDashboard';
import InvitePage from './pages/InvitePage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/" />;
  }
  
  return children;
};

/**
 * Wrapper for the invite route that checks if the user is already logged in.
 * If logged in, shows a prompt to logout before accessing the invite link.
 */
function InviteRoute() {
  const { currentUser, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  if (!currentUser) {
    // Not logged in - render the invite page directly
    return <InvitePage />;
  }

  // User IS logged in - show a prompt asking them to logout first
  const handleLogoutAndContinue = async () => {
    setLoggingOut(true);
    try {
      await logout();
      // After logout, the component re-renders and since currentUser is now null,
      // it will render InvitePage directly
    } catch (err) {
      console.error('Logout failed:', err);
      setLoggingOut(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f111a 0%, #171b26 100%)',
      fontFamily: "'Inter', sans-serif",
      padding: 20,
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 24,
        padding: '40px 32px',
        maxWidth: 400,
        width: '100%',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, margin: '0 0 8px 0' }}>
          Already Logged In
        </h2>
        <p style={{ color: '#a0aab2', fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
          You are currently logged in as <strong>{currentUser.displayName}</strong>.
          To join this invite link, you need to logout first. Your existing chats will
          be preserved on this device.
        </p>
        <button
          onClick={handleLogoutAndContinue}
          disabled={loggingOut}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: 12,
            border: 'none',
            background: loggingOut
              ? 'rgba(255,255,255,0.1)'
              : 'linear-gradient(135deg, #ff8a00, #e52e71)',
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            cursor: loggingOut ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            marginBottom: 12,
          }}
        >
          {loggingOut ? 'Logging out...' : 'Logout & Join Invite'}
        </button>
        <button
          onClick={() => window.location.href = '/dashboard'}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent',
            color: '#a0aab2',
            fontSize: 16,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <div className="App">
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/invite/:inviteCode" element={<InviteRoute />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/guest" element={<GuestDashboard />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
